import { type ModuleApiInfo } from "@client/content/selectors";
import { GravatarWithName } from "@client/controls/Avatar/Gravatar/component";
import Image from "@client/controls/Image/component";
import Loading from "@client/controls/Loading/component";
import Video from "@client/controls/Video/component";
import { usePromise } from "@client/lib/hooks";
import { type AspectRatio, getAspectRatioStyle } from "@common/controls/AspectRatioContainer/lib";
import { type Media as MediaCommon, type WithClassName } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import { getApi } from "@common/modules/system/api";
import { Common } from "@common/types";
import * as React from "react";
import { BunnyVideoContainer } from "../Video/Bunny";
import { getMediaUrl } from "./lib";

export type MediaProps = Partial<WithClassName> & {
  allowFullScreen?: boolean;
  aspectRatio?: AspectRatio;
  shouldTrackVideo?: boolean;
  sourceKey: MediaCommon.Source;
  sourceId: string;
  typeKey: MediaCommon.Type;
};

export const Media = (props: MediaProps) => {
  switch (props.typeKey) {
    case "Audio": {
      if (props.sourceKey === "SoundCloud") {
        return (
          <iframe className={props.className} src={props.sourceId} style={getAspectRatioStyle(props.aspectRatio)} />
        );
      }
      return <audio className={props.className} controls src={props.sourceId} />;
    }
    case "Image": {
      return <Image className={props.className} src={getMediaUrl(props.sourceKey, props.sourceId)} />;
    }
    case "Video": {
      switch (props.sourceKey) {
        case "Bunny": {
          return (
            <BunnyVideoContainer
              allowFullScreen
              aspectRatio={props.aspectRatio ?? "16:9"}
              shouldTrack={props.shouldTrackVideo}
              videoId={props.sourceId}
            />
          );
        }
        case "URL": {
          return (
            <Video
              allowFullScreen={props.allowFullScreen}
              aspectRatio={props.aspectRatio ?? "16/9"}
              className={props.className}
              isBackground={false}
              showControls
              shouldTrack={props.shouldTrackVideo}
              url={props.sourceId}
            />
          );
        }
        default:
          return null;
      }
    }
    default:
      return null;
  }
};

export type DynamicMediaProps = Partial<WithClassName> & {
  apiInfo: ModuleApiInfo;
  aspectRatio?: AspectRatio;
  id: string;
};

export const DynamicMedia = (props: DynamicMediaProps) => {
  const i18n = useI18n();

  const api = React.useMemo(() => getApi(i18n), [i18n]);

  const [media, isLoading] = usePromise<Common.Media.Item>({
    fn: api.getMedia,
    args: [props.id],
    shouldLoad: true
  });

  if (isLoading) {
    if (props.aspectRatio) {
      return (
        <div style={getAspectRatioStyle(props.aspectRatio)}>
          <Loading isTransparent isAbsolute />
        </div>
      );
    }

    return <Loading isTransparent />;
  }

  if (!media) return null;

  return (
    <Media
      aspectRatio={props.aspectRatio}
      className={props.className}
      sourceId={media.sourceId}
      sourceKey={media.mediaSourceKey}
      typeKey={media.mediaTypeKey}
    />
  );
};

export type ProfileImageMediaProps = {
  className?: string;
  sourceId: string | null;
  sourceKey?: MediaCommon.Source | null;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
  size?: number;
};

export const ProfileImageMedia = (props: ProfileImageMediaProps) => {
  if (!props.sourceId)
    return (
      <GravatarWithName
        className={props.className}
        isRound
        email={props.email}
        displayName={props.displayName}
        firstName={props.firstName}
        lastName={props.lastName}
        size={props.size}
      />
    );

  return (
    <div style={{ height: props.size, width: props.size }}>
      <Media
        className={props.className}
        sourceId={props.sourceId}
        sourceKey={props.sourceKey ?? "URL"}
        typeKey="Image"
      />
    </div>
  );
};

export * from "@common/components/Media/WithName/component";
